import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, Heart, Clock, MapPin } from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";
import { OfferDeposit } from '../components/OfferDeposit';
import { BALANCE_PAYMENT_NOTE, isPhotoDeposit } from '../lib/depositTerms';

export default function ReservationDevis() {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = React.useState(null);
  const [form, setForm] = React.useState({
    prenom: "", nom: "", email: "", tel: "",
    event_date: "", event_location: "", duration_hint: "",
    guests_count: "", budget_hint: "", message: "",
  });
  const [status, setStatus] = React.useState("idle"); // idle | submitting | done | error
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    api.get(`/services/${serviceSlug}`)
      .then(r => setService(r.data))
      .catch(() => setService(null));
  }, [serviceSlug]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await api.post("/quotes", {
        service_slug: serviceSlug,
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      setStatus("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de l'envoi.");
      setStatus("error");
    }
  };

  if (!service) {
    return (
      <div className="section-dark min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-accent-deep" size={32}/>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="section-dark min-h-screen pt-32 pb-20 relative">
        <Grain/>
        <div className="max-w-xl mx-auto px-6 text-center relative z-10" data-testid="devis-success">
          <div className="w-16 h-16 rounded-full bg-accent-deep/15 border border-accent-deep flex items-center justify-center mx-auto mb-6">
            <Heart size={28} className="text-accent-light"/>
          </div>
          <h1 className="font-serif text-4xl mb-3">Demande <span className="italic-accent">reçue</span>.</h1>
          <p className="text-text-cream/60 font-light mb-8">
            Merci {form.prenom}. {service.fixed_price ? 'Je vérifie la disponibilité de ta date et te réponds par email sous 24–48 h ouvrées. Ta réservation reste à confirmer.' : 'Je prépare une proposition sur mesure et te réponds par email sous 24–48 h ouvrées.'}
          </p>
          <div className="space-x-3">
            <Link to="/portfolio" data-testid="devis-success-portfolio" className="btn-ghost">Voir le portfolio</Link>
            <Link to="/" data-testid="devis-success-home" className="btn-primary">Retour à l'accueil</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-dark min-h-screen relative" data-testid="devis-page">
      <Grain/>

      <section className="relative pt-28 pb-14 px-6 md:px-12">
        <div className="max-w-[1100px] mx-auto grid lg:grid-cols-[1.3fr_1fr] gap-12">
          {/* Hero + form */}
          <div>
            <Link to="/reservation" data-testid="devis-back-services" className="eyebrow text-text-cream/50 hover:text-accent-light mb-6 inline-block">
              ← Retour aux services
            </Link>
            <div className="eyebrow mb-3 text-accent-light" data-testid="devis-request-kind">{service.fixed_price ? 'Demande de réservation' : 'Demande de devis'}</div>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] mb-5" data-testid="devis-title">
              {service.name}
            </h1>
            <p className="text-text-cream/60 font-light max-w-xl mb-10 leading-relaxed">
              {service.fixed_price ? 'Indique ta date et le lieu de ton mariage. Je vérifie la disponibilité et te réponds sous 24–48 h ouvrées. Le tarif de la formule est fixe ; la réservation sera confirmée après notre échange et le paiement de l’acompte.' : 'Parle-moi de ton événement. Je te prépare une proposition sur mesure sous 24–48 h ouvrées.'}
            </p>

            <form onSubmit={submit} className="space-y-5" data-testid="devis-form">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Prénom *" value={form.prenom} onChange={v => setForm({...form, prenom: v})} required testid="devis-prenom"/>
                <Field label="Nom *" value={form.nom} onChange={v => setForm({...form, nom: v})} required testid="devis-nom"/>
                <Field label="Email *" type="email" value={form.email} onChange={v => setForm({...form, email: v})} required testid="devis-email"/>
                <Field label="Téléphone *" value={form.tel} onChange={v => setForm({...form, tel: v})} required placeholder="+41 ..." testid="devis-tel"/>
              </div>

              <div className="pt-4 mt-6 border-t border-white/10">
                <div className="eyebrow mb-5 text-text-cream/60">Détails de l'évènement</div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Date pressentie" type="date" value={form.event_date} onChange={v => setForm({...form, event_date: v})} testid="devis-date"/>
                  <Field label="Lieu / région" value={form.event_location} onChange={v => setForm({...form, event_location: v})} placeholder="Lausanne, Montreux, ..." testid="devis-location"/>
                  {!service.fixed_price && <Field label="Durée souhaitée" value={form.duration_hint} onChange={v => setForm({...form, duration_hint: v})} placeholder="Journée entière, 6-8 h ..." testid="devis-duration"/>}
                  <Field label="Nombre d'invités" value={form.guests_count} onChange={v => setForm({...form, guests_count: v})} placeholder="80, 120, ..." testid="devis-guests"/>
                  {!service.fixed_price && <div className="md:col-span-2">
                    <Field label="Budget indicatif" value={form.budget_hint} onChange={v => setForm({...form, budget_hint: v})} placeholder="2'500, 3'500 CHF ..." testid="devis-budget"/>
                  </div>}
                </div>
              </div>

              <div>
                <label htmlFor="devis-message" className="block eyebrow mb-2">Parle-moi de ton projet</label>
                <textarea
                  id="devis-message"
                  data-testid="devis-message"
                  rows={4}
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  placeholder="Ambiance, style souhaité, moments clés à ne pas manquer, inspirations..."
                  className="w-full bg-white/5 border border-white/15 rounded px-4 py-3 text-text-cream placeholder:text-text-cream/30 focus:outline-none focus:border-accent-deep font-light"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-red-300 text-sm" data-testid="devis-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "submitting" || !form.prenom || !form.nom || !form.email || !form.tel}
                className="btn-primary disabled:opacity-50"
                data-testid="devis-submit"
              >
                {status === "submitting" ? <Loader2 className="animate-spin mr-2" size={16}/> : null}
                Envoyer ma demande <ArrowRight size={14} className="ml-2"/>
              </button>
              <p className="text-xs text-text-cream/40 font-light">
                Réponse personnelle sous 24-48 h ouvrées · Appel découverte offert si nécessaire
              </p>
            </form>
          </div>

          {/* Sidebar */}
          <div>
            <div className="sticky top-24 bg-white/[0.03] border border-white/10 rounded p-7">
              <div className="eyebrow text-text-cream/50 mb-2" data-testid="devis-price-label">{service.fixed_price ? 'Tarif fixe de la formule' : 'À partir de'}</div>
              <div className="font-serif text-4xl sm:text-5xl text-accent-light mb-5" data-testid="devis-price">CHF {service.price_chf.toLocaleString("fr-CH")}</div>
              <p className="text-text-cream/60 text-sm font-light mb-6 leading-relaxed">{service.description}</p>

              <div className="space-y-3 text-sm text-text-cream/70">
                <Check2 label={`Durée : ${service.duration_min >= 60 ? Math.floor(service.duration_min/60) + ' h' : service.duration_min + ' min'}`}/>
                <Check2 testid="devis-deposit-terms" label={`Acompte ${service.deposit_pct} % ${service.fixed_price ? 'à la confirmation de la réservation' : 'à la validation du devis'}`}/>
                <OfferDeposit offer={service} id="devis-deposit-amount"/>
                {isPhotoDeposit(service) && <p className="text-xs leading-relaxed" data-testid="devis-balance-payment-note">{BALANCE_PAYMENT_NOTE}</p>}
                <Check2 label="Clé USB gravée + galerie privée en ligne"/>
                <Check2 label="Déplacement inclus Canton de Vaud"/>
                <Check2 label="Appel découverte offert (15 min)"/>
              </div>

              <div className="mt-7 pt-6 border-t border-white/10 text-xs text-text-cream/50 font-light leading-relaxed">
                Les mariages demandent une préparation sur mesure. On échange, je comprends ton univers,
                je te propose un plan de reportage détaillé — puis seulement, tu décides.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Check2({ label, testid }) {
  return (
    <div className="flex items-start gap-2.5">
      <Check size={14} className="text-accent-light flex-shrink-0 mt-0.5"/>
      <span data-testid={testid}>{label}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder = "", testid }) {
  return (
    <div>
      <label htmlFor={testid} className="block eyebrow mb-2">{label}</label>
      <input
        id={testid}
        data-testid={testid}
        lang="fr-CH"
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/15 rounded px-4 py-3 text-text-cream placeholder:text-text-cream/30 focus:outline-none focus:border-accent-deep font-light"
      />
    </div>
  );
}
